import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import { IMessageType } from '@protobuf-ts/runtime'
import { FinishedUnaryCall, RpcError, RpcOptions, RpcTransport, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { Facets } from 'pbts/c1/api/search/v1/facet'
import { Any } from 'pbts/google/protobuf/any'
import { Code } from 'pbts/google/rpc/code'
import { Dispatch, useContext, useEffect, useState } from 'react'
import { IntlShape } from 'react-intl/lib'
import { CancelFunc, wrapDispatch } from './cancel'
import { AuthContext } from './context/app'
import { getNotifyTokens, NotifyClient } from './notify'
import { codeToString, Status } from './rpc'

export const Timeout = 29000

interface ClientConstructor<T> {
    new(_transport: RpcTransport): T
}

export type RequestMetadata = {
    currentPageToken: string
    nextPageToken: string
    facets?: Facets
}

export type ActionStatus<T> =
    | {
        status: Status.INIT
    }
    | {
        status: Status.OK
        item: T
        metadata?: RequestMetadata
    }
    | {
        status: Status.ERROR
        error: RpcError
        // TODO degzhaus remove this any
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        request?: any
    }
    | {
        status: Status.LOADING
    }

export const useTransport = (): GrpcWebFetchTransport | undefined => {
    const authContext = useContext(AuthContext)
    return authContext.get('transport') || undefined
}

export const initialActionReturn = <T>(): ActionStatus<T> => {
    return {
        status: Status.INIT,
    }
}

export const useClientRPC = <T>(TsClientConstructor: ClientConstructor<T>): T | undefined => {
    const [client, setClient] = useState<T>()
    const transport = useTransport()

    useEffect((): void => {
        if (!transport) {
            return
        }

        setClient(new TsClientConstructor(transport))
    }, [TsClientConstructor, transport])
    return client
}

export const useUnauthedClientRRPC = <T>(TsClientConstructor: ClientConstructor<T>): T | undefined => {
    const [client, setClient] = useState<T>()

    useEffect((): void => {
        const transport = new GrpcWebFetchTransport({
            baseUrl: window.origin,
            fetchInit: { credentials: 'include' },
        })

        setClient(new TsClientConstructor(transport))
    }, [TsClientConstructor])
    return client
}

// actionInvoke - wait for request and update an existing notify subscriber if found
export const actionInvoke = async <ReqT extends object = object, RespT extends object = object>(
    input: ReqT,
    invokeFn: (input: ReqT, options?: RpcOptions) => UnaryCall<ReqT, RespT>,
    notifyClient?: NotifyClient,
): Promise<ActionStatus<RespT>> => {
    let resp: FinishedUnaryCall<ReqT, RespT>
    try {
        resp = await invokeFn(input, { timeout: Timeout })
    } catch (err) {
        return {
            status: Status.ERROR,
            error: err as RpcError,
            request: input,
        }
    }
    notifyClient?.runHandlers()
    return {
        status: Status.OK,
        item: resp.response,
    }
}

// NOTE: uses 'extend object' because upstream protobuf-ts is using object as its type for UnaryCall
export const unaryInvoke = <ReqT extends object = object, RespT extends object = object>(
    input: ReqT,
    invokeFn: (input: ReqT, options?: RpcOptions) => UnaryCall<ReqT, RespT>,
    dispatch: Dispatch<ActionStatus<RespT>>,
    notifyClient?: NotifyClient,
): CancelFunc => {
    const aborter = new AbortController()

    const [dispatcher, cancelFunc] = wrapDispatch(dispatch)
    let notifyCleanup: (() => void) | undefined

    dispatcher({
        status: Status.LOADING,
    })

    const runOperation = async (doSubscribe: boolean): Promise<void> => {
        let resp: FinishedUnaryCall<ReqT, RespT>
        try {
            resp = await invokeFn(input, {
                timeout: Timeout,
                abort: aborter.signal,
            })
        } catch (err) {
            if ((err as RpcError).code === 'INTERNAL') {
                // eslint-disable-next-line no-ex-assign
                err = new RpcError(JSON.stringify(err), codeToString(Code.INTERNAL))
            }
            dispatcher({
                status: Status.ERROR,
                error: err as RpcError,
                request: input,
            })
            return
        }
        dispatcher({
            status: Status.OK,
            item: resp.response,
        })
        const notifyTokens = getNotifyTokens(resp.headers || {})
        if (doSubscribe) {
            notifyCleanup = notifyClient?.subscribe(notifyTokens, () => runOperation(false))
        }
    }
    runOperation(true)

    return (): void => {
        cancelFunc()
        aborter.abort()
        if (notifyCleanup) {
            notifyCleanup()
        }
    }
}

interface reqPaginated {
    pageToken: string
}

interface respPaginated {
    nextPageToken: string
    facets?: Facets
}

interface collector<T> {
    reset(): void

    push(input: T): void
}

export const unaryListCollect = <ReqT extends reqPaginated, RespT extends respPaginated>(
    input: ReqT,
    invokeFn: (input: ReqT, options?: RpcOptions) => UnaryCall<ReqT, RespT>,
    collector: collector<RespT>,
    dispatch: Dispatch<ActionStatus<collector<RespT>>>,
    notifyClient: NotifyClient | undefined = undefined,
): CancelFunc => {
    const aborter = new AbortController()

    const [dispatcher, cancelFunc] = wrapDispatch(dispatch)
    let notifyCleanup: (() => void) | undefined

    dispatcher({
        status: Status.LOADING,
    })

    const runOperation = async (doSubscribe: boolean): Promise<void> => {
        collector.reset()
        let nextPageToken = ''
        let notifyTokens: string[] | undefined = []
        for (; ;) {
            let resp: FinishedUnaryCall<ReqT, RespT>
            try {
                resp = await invokeFn(input, {
                    timeout: Timeout,
                    abort: aborter.signal,
                })
            } catch (err) {
                dispatcher({
                    status: Status.ERROR,
                    error: err as RpcError,
                })
                return
            }

            collector.push(resp.response)
            notifyTokens = getNotifyTokens(resp.headers || {})
            nextPageToken = resp.response.nextPageToken
            input.pageToken = nextPageToken
            if (!nextPageToken || nextPageToken === '') {
                break
            }
        }
        dispatcher({
            status: Status.OK,
            item: collector,
        })
        if (doSubscribe) {
            notifyCleanup = notifyClient?.subscribe(notifyTokens, () => runOperation(false))
        }
    }
    runOperation(true)

    return (): void => {
        cancelFunc()
        aborter.abort()
        if (notifyCleanup) {
            notifyCleanup()
        }
    }
}

export const unaryMultiRequest = <ReqT extends object = object, RespT extends object = object>(
    input: ReqT[],
    invokeFn: (input: ReqT, options?: RpcOptions) => UnaryCall<ReqT, RespT>,
    collector: collector<RespT>,
    dispatch: Dispatch<ActionStatus<collector<RespT>>>,
    notifyClient: NotifyClient | undefined = undefined,
): CancelFunc => {
    const aborter = new AbortController()

    const [dispatcher, cancelFunc] = wrapDispatch(dispatch)
    let notifyCleanup: (() => void) | undefined

    dispatcher({
        status: Status.LOADING,
    })

    const runOperation = async (doSubscribe: boolean): Promise<void> => {
        collector.reset()
        let notifyTokens: string[] | undefined = []
        for (const item of input) {
            let resp: FinishedUnaryCall<ReqT, RespT>
            try {
                resp = await invokeFn(item, {
                    timeout: Timeout,
                    abort: aborter.signal,
                })
            } catch (err) {
                dispatcher({
                    status: Status.ERROR,
                    error: err as RpcError,
                })
                return
            }

            collector.push(resp.response)
            notifyTokens = getNotifyTokens(resp.headers || {})
        }
        dispatcher({
            status: Status.OK,
            item: collector,
        })
        if (doSubscribe) {
            notifyCleanup = notifyClient?.subscribe(notifyTokens, () => runOperation(false))
        }
    }
    runOperation(true)

    return (): void => {
        cancelFunc()
        aborter.abort()
        if (notifyCleanup) {
            notifyCleanup()
        }
    }
}

export const unaryListCollectSinglePage = <ReqT extends reqPaginated, RespT extends respPaginated>(
    input: ReqT,
    invokeFn: (input: ReqT, options?: RpcOptions) => UnaryCall<ReqT, RespT>,
    collector: collector<RespT>,
    dispatch: Dispatch<ActionStatus<collector<RespT>>>,
    notifyClient: NotifyClient | undefined = undefined,
): CancelFunc => {
    const aborter = new AbortController()

    const [dispatcher, cancelFunc] = wrapDispatch(dispatch)
    let notifyCleanup: (() => void) | undefined

    dispatcher({
        status: Status.LOADING,
    })

    const runOperation = async (doSubscribe: boolean): Promise<void> => {
        let notifyTokens: string[] | undefined = []
        let resp: FinishedUnaryCall<ReqT, RespT>
        try {
            resp = await invokeFn(input, {
                timeout: Timeout,
                abort: aborter.signal,
            })
        } catch (err) {
            dispatcher({
                status: Status.ERROR,
                error: err as RpcError,
            })
            return
        }

        collector.reset()
        collector.push(resp.response)
        notifyTokens = getNotifyTokens(resp.headers || {})

        dispatcher({
            status: Status.OK,
            item: collector,
            metadata: {
                currentPageToken: input.pageToken,
                nextPageToken: resp.response.nextPageToken,
                facets: resp.response.facets,
            },
        })
        if (doSubscribe) {
            notifyCleanup = notifyClient?.subscribe(notifyTokens, () => runOperation(false))
        }
    }
    runOperation(true)

    return (): void => {
        cancelFunc()
        aborter.abort()
        if (notifyCleanup) {
            notifyCleanup()
            notifyCleanup = undefined
        }
    }
}

// TODO(pquerna): make array variant
// TOOD(pquerna): caching context -- we often unmarshal the same Any to a type in a loop
// NOTE: uses 'extend object' because upstream protobuf-ts is using object as its type for UnaryCall
export const expandValue = <T extends object = object>(
    path: string | undefined,
    typ: IMessageType<T>,
    values: Any[],
): T | undefined => {
    if (!path) {
        return undefined
    }
    const indexes = expandIndexFromPath(path)
    if (indexes.length !== 1) {
        return
    }

    try {
        return Any.unpack<T>(values[indexes[0]], typ)
    } catch (err) {
        return // eslint-disable-line no-useless-return
    }
}

// NOTE: uses 'extend object' because upstream protobuf-ts is using object as its type for UnaryCall
export const expandValueList = <T extends object = object>(
    path: string | undefined,
    typ: IMessageType<T>,
    values: Any[],
): T[] | undefined => {
    if (!path) {
        return undefined
    }
    const indexes = expandIndexFromPath(path)
    if (!indexes.length) {
        return
    }

    const items: T[] = []
    for (const index of indexes) {
        try {
            items.push(Any.unpack<T>(values[index], typ))
        } catch (err) {
            return
        }
    }

    return items
}

/**
 * @param path e.g. "10", "$.expanded[1]", "$.expanded[0,2,6,9]"
 * @returns array of index(es)
 */
export const expandIndexFromPath = (path: string): number[] => {
    if (path === '') {
        return []
    }
    const pathValue = parseInt(path, 10)
    if (isFinite(pathValue)) {
        return [pathValue]
    }

    const matched = /^\$\.expanded\[(\d+[,\d+]*)\]$/.exec(path)?.[1]
    if (!matched) {
        return []
    }
    // `matched` can be one number (e.g. "1") or multiple delimited by commas (e.g. "1,3,7")
    const delimitedNums = matched.split(',')
    if (delimitedNums?.length) {
        const pathValues: number[] = []
        for (const num of delimitedNums) {
            const v = parseInt(num, 10)
            if (isFinite(v)) {
                pathValues.push(v)
            }
        }
        return pathValues
    }

    return []
}

export class Collector<T> {
    buf: T[] = []

    reset(): void {
        this.buf = []
    }

    buffer(): T[] {
        const b = this.buf
        this.buf = []
        return b
    }
}

export const i18nErrorMessage = ({
    intl,
    errorReturn,
}: {
    intl: IntlShape
    errorReturn: {
        status: Status.ERROR
        error: RpcError
    }
}): string => {
    return intl.formatMessage(
        {
            id: 'i18_error_message_format',
            defaultMessage: 'Error({code}): {message}',
        },
        {
            code: errorReturn.error.code,
            message: errorReturn.error.message,
        },
    )
}
